<div class="container-fluid">
  <div class="row">
    <div class="col-12 px-0">
      <div class="card p-0 border-0">
        <div class="card-body p-3">
          <!-- Header Section -->
          <div class="d-flex flex-row flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h1 class="card-title mb-1">Sales Team Management</h1>
              <span class="sub-text">Who reports to whom, from Admin down to Sales Officers</span>
            </div>
          </div>

          @switch (loadState) {
            @case ('loading') {
              <div class="page-loading">
                <app-loading-spinner></app-loading-spinner>
              </div>
            }

            @case ('forbidden') {
              <app-not-authorized
                title="Only admins can view the sales hierarchy"
                message="Ask an admin if you need to see who reports to whom." />
            }

            @case ('error') {
              <div class="empty-state-container">
                <i class="bi bi-exclamation-triangle"></i>
                <h5 class="text-dark fw-bold">Could not load the sales hierarchy</h5>
                <p class="text-muted mb-4">Check your connection and try again.</p>
                <button
                  type="button"
                  class="btn btn-primary d-flex align-items-center gap-2 px-4"
                  (click)="loadHierarchy()">
                  <i class="bi bi-arrow-clockwise"></i>
                  Retry
                </button>
              </div>
            }

            @case ('ready') {
              <div class="alert alert-info d-flex align-items-start mb-3" role="alert">
                <i class="bi bi-info-circle-fill me-2 mt-1"></i>
                <div>
                  Sales Officers report to Area Sales Managers (ASMs), ASMs report to Regional Sales Managers (RSMs),
                  and RSMs report to Admin. If someone has no manager assigned, they will appear under Unassigned. Use
                  <strong>Assign manager</strong>
                  or
                  <strong>Change manager</strong>
                  on a row to set who someone reports to, or
                  <strong>Select</strong>
                  to give several people of the same level one manager at once. Inactive employees will remain visible
                  in grey, so their reporting employees are still visible.
                </div>
              </div>

              @if (team.length === 0) {
                <div class="empty-state-container">
                  <i class="bi bi-diagram-3"></i>
                  <h5 class="text-dark fw-bold">No sales team members yet</h5>
                  <p class="text-muted mb-0">
                    Regional Sales Managers, Area Sales Managers and Sales Officers appear here once they are added as
                    employees.
                  </p>
                </div>
              } @else {
                <!-- Summary: counts per level and assignment progress -->
                <section class="summary-panel mb-3" aria-label="Sales team summary">
                  <div class="summary-stats">
                    @for (stat of stats; track stat.tone) {
                      <div class="summary-stat" [ngClass]="'tone-' + stat.tone">
                        <span class="summary-stat-label">
                          @if (stat.tone !== 'all') {
                            <span class="tone-dot" aria-hidden="true"></span>
                          }
                          {{ stat.label }}
                        </span>
                        <span class="summary-stat-value">{{ stat.count }}</span>
                      </div>
                    }
                  </div>

                  <div class="summary-progress">
                    <span class="progress-caption">
                      <strong>{{ placedCount }} of {{ summary.totalCount }}</strong>
                      placed in the reporting chain
                    </span>
                    <div
                      class="progress-track"
                      role="img"
                      [attr.aria-label]="placedCount + ' of ' + summary.totalCount + ' placed in the reporting chain'">
                      @for (segment of progressSegments; track segment.tone) {
                        <span
                          class="progress-segment"
                          [ngClass]="'tone-' + segment.tone"
                          [style.width.%]="segment.percent"
                          [title]="segment.label"></span>
                      }
                    </div>
                    @if (summary.unassignedCount > 0) {
                      <span class="progress-pending">{{ summary.unassignedCount }} need a manager</span>
                    } @else {
                      <span class="progress-done">
                        <i class="bi bi-check-circle-fill"></i>
                        Everyone has a manager
                      </span>
                    }
                  </div>
                </section>

                <!-- Tree -->
                <div class="sales-tree">
                  <div class="tree-root">
                    @if (isSelecting) {
                      <span class="tree-select" aria-hidden="true"></span>
                    }
                    <span
                      class="tree-stem has-stem"
                      [class.path-stem]="!!focusedName"
                      [style.--path-stem]="'var(--tone-' + focusTopTone + ')'">
                      <span class="tree-root-avatar" aria-hidden="true"><i class="bi bi-person-gear"></i></span>
                    </span>
                    <div class="tree-heading-text">
                      <span class="tree-root-title">Admin</span>
                      <span class="tree-heading-hint">Top of the reporting chain</span>
                    </div>
                    <div class="tree-root-actions">
                      @if (focusedName) {
                        <button type="button" class="focus-chip" title="Clear focus (Esc)" (click)="onClearFocus()">
                          <i class="bi bi-bullseye"></i>
                          <span class="focus-chip-text">Focused on {{ focusedName }}</span>
                          <i class="bi bi-x-lg"></i>
                        </button>
                      }
                      @if (!isSelecting) {
                        <button
                          type="button"
                          class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
                          (click)="onStartSelecting()">
                          <i class="bi bi-check2-square"></i>
                          Select
                        </button>
                      }
                      <button
                        type="button"
                        class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
                        (click)="onExpandAll()">
                        <i class="bi bi-arrows-expand"></i>
                        Expand all
                      </button>
                      <button
                        type="button"
                        class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
                        (click)="onCollapseAll()">
                        <i class="bi bi-arrows-collapse"></i>
                        Collapse all
                      </button>
                    </div>
                  </div>

                  <!-- Regional Sales Managers, straight off Admin -->
                  @for (row of rsmRows; track row.node.id) {
                    <ng-container *ngTemplateOutlet="treeRow; context: { $implicit: row }" />
                  }

                  <!-- Unassigned -->
                  <div
                    class="tree-group-heading"
                    [class.is-expanded]="isUnassignedGroupExpanded"
                    [class.is-dimmed]="isUnassignedGroupDimmed"
                    (click)="onToggle(unassignedGroupId)">
                    @if (isSelecting) {
                      <span class="tree-select" aria-hidden="true"></span>
                    }
                    <div class="tree-indent" aria-hidden="true">
                      <span
                        class="tree-elbow is-last"
                        [class.path-up]="isFocusInUnassignedGroup"
                        [class.path-across]="isFocusInUnassignedGroup"
                        [style.--path-line]="'var(--tone-' + focusTopTone + ')'"
                        [style.--path-across]="'var(--tone-' + focusTopTone + ')'"></span>
                    </div>
                    <span
                      class="tree-stem"
                      [class.has-stem]="unassignedRows.length > 0"
                      [class.path-stem]="isFocusInUnassignedGroup && unassignedRows.length > 0"
                      [class.path-across]="isFocusInUnassignedGroup"
                      [style.--path-stem]="'var(--tone-' + focusTopTone + ')'"
                      [style.--path-across]="'var(--tone-' + focusTopTone + ')'">
                      <button
                        type="button"
                        class="tree-toggle"
                        [attr.aria-expanded]="isUnassignedGroupExpanded"
                        [attr.aria-label]="(isUnassignedGroupExpanded ? 'Collapse' : 'Expand') + ' Unassigned'">
                        <i class="bi bi-chevron-right"></i>
                      </button>
                    </span>
                    <div class="tree-heading-text">
                      <span class="tree-group-title">
                        Unassigned
                        <span class="count-pill" [class.is-pending]="summary.unassignedCount > 0">
                          {{ summary.unassignedCount }}
                        </span>
                      </span>
                      <span class="tree-heading-hint">
                        @if (summary.unassignedCount > 0) {
                          Area Sales Managers and Sales Officers with no manager yet
                        } @else {
                          Everyone has a manager
                        }
                      </span>
                    </div>
                  </div>

                  @for (row of unassignedRows; track row.node.id) {
                    <ng-container *ngTemplateOutlet="treeRow; context: { $implicit: row }" />
                  }
                </div>

                <!-- Select mode: stays in view while scrolling the tree -->
                @if (isSelecting) {
                  <div class="selection-bar" role="region" aria-label="Select people">
                    <span class="selection-count">
                      @if (selectedIds.size === 0) {
                        Tick the people who need the same manager.
                      } @else {
                        <strong>{{ selectionLabel }} selected</strong>
                      }
                    </span>
                    <div class="d-flex gap-2 ms-auto">
                      <button type="button" class="btn btn-light btn-sm" (click)="onStopSelecting()">Cancel</button>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm d-flex align-items-center gap-2"
                        [disabled]="selectedIds.size === 0"
                        (click)="onBulkAssign()">
                        <i class="bi bi-person-plus"></i>
                        Assign manager
                      </button>
                    </div>
                  </div>
                }
              }
            }
          }
        </div>
      </div>
    </div>
  </div>
</div>

<!-- One person in the tree, with their checkbox and manager actions. RSMs get neither. -->
<ng-template #treeRow let-row>
  <app-sales-team-row
    [row]="row"
    [selectMode]="isSelecting"
    [selectable]="canSelect(row.node)"
    [selected]="selectedIds.has(row.node.id)"
    (selectedChange)="onSelectionChange(row.node, $event)"
    (toggle)="onToggle($event)"
    (select)="onSelect($event)">
    <!-- In select mode the bar carries the action, so rows drop theirs. -->
    @if (canManage(row.node) && !isSelecting) {
      <button
        type="button"
        class="btn btn-soft-primary btn-sm row-action"
        [disabled]="removingIds.has(row.node.id)"
        (click)="onAssignManager(row.node)">
        <i
          class="bi"
          [class.bi-arrow-left-right]="hasManager(row.node)"
          [class.bi-person-plus]="!hasManager(row.node)"></i>
        {{ hasManager(row.node) ? 'Change manager' : 'Assign manager' }}
      </button>
      @if (hasManager(row.node)) {
        <button
          type="button"
          class="btn btn-sm row-action-remove"
          ngbTooltip="Remove manager"
          triggers="hover"
          container="body"
          [openDelay]="200"
          #removeTip="ngbTooltip"
          [attr.aria-label]="'Remove manager for ' + row.node.name"
          [disabled]="removingIds.has(row.node.id)"
          (click)="removeTip.close(); onRemoveManager(row.node)">
          @if (removingIds.has(row.node.id)) {
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          } @else {
            <i class="bi bi-person-dash"></i>
          }
        </button>
      }
    }
  </app-sales-team-row>
</ng-template>
